'use client';

import type { CSSProperties, ReactNode } from 'react';
import { Swiper } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { sanitizeHeroDesign, type HeroDesign } from '@/lib/theme-settings';

interface HeroCarouselShellProps {
  /** Theme-owned classes for the carousel's outer box (framing, background, theme hook class). */
  variantClassName: string;
  /** Validated admin overrides (Phase 4). Heights are applied with CSS custom properties + media queries. */
  design?: HeroDesign;
  autoplay?: boolean;
  showNavigation?: boolean;
  showPagination?: boolean;
  /** The theme's <SwiperSlide> elements — the shell knows nothing about how a slide looks. */
  children: ReactNode;
}

/**
 * The hero carousel ENGINE shared by every theme: Swiper configuration (fade, loop, autoplay, arrows, dynamic
 * pagination) and the CSS that is identical across themes (arrow base look, pagination, the admin height overrides,
 * CTA hover). What a slide looks like, how the carousel is framed and where the arrows sit are THEME decisions
 * (src/themes/theme01/, src/themes/theme02/): the theme passes its own slides, `variantClassName` and stylesheet.
 */
export function HeroCarouselShell({
  variantClassName,
  design: designProp,
  autoplay = true,
  showNavigation = true,
  showPagination = true,
  children,
}: HeroCarouselShellProps) {
  // Last line of defense: whatever arrives here, only validated numbers/#RRGGBB reach a style.
  const design = sanitizeHeroDesign(designProp);

  // CSS custom properties are emitted only for the heights that are set (numbers validated above).
  const heightVars: Record<string, string> = {};
  if (design.desktopHeight !== undefined) heightVars['--hero-h-desktop'] = `${design.desktopHeight}px`;
  if (design.mobileHeight !== undefined) heightVars['--hero-h-mobile'] = `${design.mobileHeight}px`;
  const hasHeightVars = Object.keys(heightVars).length > 0;

  return (
    <div
      className={`hero-carousel relative w-full max-w-full h-auto overflow-hidden ${variantClassName}`}
      style={hasHeightVars ? (heightVars as CSSProperties) : undefined}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        navigation={showNavigation}
        pagination={{
          clickable: true,
          dynamicBullets: true,
          enabled: showPagination,
        }}
        autoplay={
          autoplay
            ? {
                delay: 5000,
                disableOnInteraction: false,
              }
            : false
        }
        loop
        className="w-full max-w-full"
      >
        {children}
      </Swiper>

      {/* Navigation arrows / pagination / height overrides shared by every theme. */}
      <style jsx>{`
        :global(.hero-carousel .swiper),
        :global(.hero-carousel .swiper-wrapper),
        :global(.hero-carousel .swiper-slide) {
          max-width: 100%;
        }

        :global(.hero-carousel .swiper-button-next),
        :global(.hero-carousel .swiper-button-prev) {
          color: #ffffff;
          background: rgba(60, 80, 224, 0.7);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          top: 50%;
          transform: translateY(-50%);
          transition: all 200ms ease-out;
        }

        :global(.hero-carousel .swiper-button-next:hover),
        :global(.hero-carousel .swiper-button-prev:hover) {
          background: rgba(28, 63, 183, 0.9);
          box-shadow: 0 6px 24px rgba(60, 80, 224, 0.4);
        }

        :global(.hero-carousel .swiper-button-next::after),
        :global(.hero-carousel .swiper-button-prev::after) {
          font-size: 24px;
          font-weight: bold;
        }

        /* Marble.Theme.01 and Marble.Theme.02 (every carousel that is NOT the premium one): the arrows are WHITE GLYPHS ONLY — no
           disc, no oval, no square, no coloured or translucent background, in ANY state. This is a fixed design contract, not a
           setting: nothing here reads Admin/theme configuration (there is none for the arrows), and the colour above is not the
           domain's. The glyph is Swiper's own SVG (fill: currentColor), so the white comes from the button's colour; the button
           keeps its 48px box, so the transparent tap target is exactly what it was. A neutral, colourless drop shadow keeps the
           chevron legible over bright photos. Theme.03 (.hero-carousel--premium) is deliberately outside this selector: it styles
           its own arrows (theme03.css). The extra :not() class makes every rule outrank the shared rules above in every state. */
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next:hover),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev:hover),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next:focus),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev:focus),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next:active),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev:active),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next.swiper-button-disabled),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev.swiper-button-disabled) {
          color: #ffffff;
          background: transparent;
          border: 0;
          border-radius: 0;
          box-shadow: none;
          -webkit-tap-highlight-color: transparent;
        }

        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev) {
          filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.55));
        }

        /* Keyboard focus, if a button is ever focusable: an outline (never a background). */
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-next:focus-visible),
        :global(.hero-carousel:not(.hero-carousel--premium) .swiper-button-prev:focus-visible) {
          outline: 2px solid #ffffff;
          outline-offset: 2px;
        }

        :global(.hero-carousel .swiper-pagination-bullet) {
          width: 4px;
          height: 1px;
          background: #ddd;
          opacity: 1;
          border-radius: 2px;
          transition: all 200ms ease-out;
          margin: 0 6px;
        }

        :global(.hero-carousel .swiper-pagination-bullet-active) {
          width: 5.5px;
          height: 2px;
          background: #3c50e0;
          box-shadow: 0 0 0 3px rgba(60, 80, 224, 0.1);
        }

        :global(.hero-carousel .swiper-pagination) {
          bottom: 20px !important;
        }

        /* Admin height overrides (only present when a height is configured). Mobile = below the md
           breakpoint (768px), desktop = md and up — the same split the slide's own classes use. Height
           is fixed to the configured value; the aspect-ratio/max-height the wide layout applies at md+
           are released so the setting really controls the height. Background layers are absolutely
           positioned with bg-cover, so the image crops (never stretches) at any height. */
        @media (max-width: 767px) {
          :global(.hero-carousel .hero-h-m) {
            height: var(--hero-h-mobile);
            min-height: var(--hero-h-mobile);
            aspect-ratio: auto;
          }
        }

        @media (min-width: 768px) {
          :global(.hero-carousel .hero-h-d) {
            height: var(--hero-h-desktop);
            min-height: var(--hero-h-desktop);
            max-height: none;
            aspect-ratio: auto;
          }
        }

        /* CTA with an Admin color override: an inline background cannot be darkened by the
           hover:bg-primary-dark utility, so keep hover feedback with a filter instead. */
        :global(.hero-carousel .hero-cta-custom:hover) {
          filter: brightness(0.92);
        }
      `}</style>
    </div>
  );
}
